// The conditions grid, rendered for paper.
//
// WHY THIS EXISTS. The console's best instrument is the conditions table — the
// three-hourly grid with a number in every cell and the cell tinted by how much
// of the operating limit that number uses. It is the thing the client asked for
// by name. And until now the printable report did not contain it: the report was
// two charts and roughly five thousand characters of prose, of which a single
// briefing block was two thousand. A document that is 42% one essay is not a
// forecast product, it is an essay with a forecast attached.
//
// WHY HTML RATHER THAN THE CANVAS. panel/table.ts draws to a canvas because it
// scrolls a week of columns at sixty frames a second and only paints what is on
// screen — the right trade for a live surface, the wrong one for paper, where
// nothing scrolls, every column must be present at once, and the output has to
// survive being printed at 300 dpi and read as selectable text. So this renders
// the same DATA through the same colour functions into a table element.
//
// It reuses panel/rows.ts and panel/heat.ts rather than reimplementing either.
// That is deliberate and it is the whole point: a report whose numbers or
// colours were computed by a second code path would eventually disagree with the
// screen, and the client would be holding the disagreement in their hand.

import { buildTabs, type Row, type TableInput } from "../panel/rows.js";
import {
  gradeFill, parseColor, readableInk, toCss, GRADE_WEIGHTS, type RGB,
} from "../panel/heat.js";
import { WORK_GRADES } from "../ops/workability.js";
import { cssVar } from "../chart/marks.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className !== undefined) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

/** Grade index for a 0..1 probability, matching WORK_GRADES' own ordering. */
function gradeIndexOf(p: number): number {
  for (let i = 0; i < WORK_GRADES.length; i++) {
    if (p >= WORK_GRADES[i].min) return i;
  }
  return WORK_GRADES.length - 1;
}

/**
 * Render one tab's rows as a printable table.
 *
 * `columns` are indices into the forecast; `label` turns one into its column
 * heading. The caller owns both so the report's own window and time convention
 * govern, rather than this module inventing a second axis.
 */
export function conditionGrid(
  input: TableInput,
  label: (i: number) => string,
  tabId = "conditions",
): HTMLElement | null {
  const tab = buildTabs(input).find((t) => t.id === tabId);
  if (tab === undefined || tab.rows.length === 0 || input.columns.length === 0) return null;

  // parseColor returns null on anything it cannot read; the report must render
  // regardless, so each falls back to the literal it was asked for.
  const rgb = (name: string, fallback: string, lit: RGB): RGB =>
    parseColor(cssVar(name, fallback)) ?? lit;
  const surface = rgb("--surface", "#ffffff", [255, 255, 255]);
  const inks: RGB[] = [
    rgb("--ink", "#16232e", [22, 35, 46]),
    rgb("--paper", "#f3f4f2", [243, 244, 242]),
  ];

  const wrap = el("div", "rp-gridwrap");
  const tbl = el("table", "rp-grid");

  const thead = el("thead");
  const hr = el("tr");
  hr.appendChild(el("th", "rp-gridstub", ""));
  for (const i of input.columns) hr.appendChild(el("th", undefined, label(i)));
  thead.appendChild(hr);
  tbl.appendChild(thead);

  const tb = el("tbody");
  for (const row of tab.rows) {
    // The daylight and tide rows are drawn shapes on screen, not numbers; on
    // paper they would be a row of empty cells claiming to say something.
    if (row.kind !== "value") continue;
    const tr = el("tr");
    const stub = el("th", "rp-gridstub");
    stub.appendChild(el("div", "rp-gridlab", row.label));
    const sub = row.note ?? row.unit;
    if (sub) stub.appendChild(el("div", "rp-gridunit", sub));
    tr.appendChild(stub);

    for (let c = 0; c < input.columns.length; c++) {
      const cell = row.cells[c];
      const td = el("td");
      if (cell === undefined || cell.text === "") {
        tr.appendChild(td);
        continue;
      }
      td.textContent = cell.text + (cell.sub === undefined ? "" : ` ${cell.sub}`);
      // Colour only where the console would colour it: a graded cell on the
      // limit scale. An ungraded number stays plain rather than borrowing a tint
      // that would read as a verdict.
      const p = cell.p;
      if (row.scale === "limit" && p !== null && p !== undefined) {
        const gi = gradeIndexOf(p);
        const g = WORK_GRADES[gi];
        const fill = gradeFill(gi, rgb(g.varName, g.fallback, [128, 128, 128]), surface, GRADE_WEIGHTS[gi]);
        td.style.background = toCss(fill);
        td.style.color = toCss(readableInk(fill, inks));
      }
      if (cell.mark !== undefined) {
        const m = el("span", "rp-gridmark", cell.mark);
        if (cell.markTitle !== undefined) m.title = cell.markTitle;
        td.appendChild(m);
      }
      tr.appendChild(td);
    }
    tb.appendChild(tr);
  }
  tbl.appendChild(tb);
  wrap.appendChild(tbl);
  return wrap;
}

/** The rows a printed grid drops, named, so their absence is not a mystery. */
export function gridOmissions(input: TableInput, tabId = "conditions"): string[] {
  const tab = buildTabs(input).find((t) => t.id === tabId);
  if (tab === undefined) return [];
  return tab.rows.filter((r: Row) => r.kind !== "value").map((r) => r.label);
}
